<template>
  <div class="print-root print-brackets-page">
    <div v-if="error" class="error-message">
      <p>Error loading brackets. Please try again.</p>
    </div>

    <div v-else-if="loading" class="loading-message">
      <p>Loading brackets...</p>
    </div>

    <template v-else-if="!error && !loading && tournament && brackets.length > 0">
      <div
        v-for="bracket in brackets"
        :key="bracket.id"
        class="print-category"
      >
        <!-- Competitor list for this discipline (printed first) -->
        <div class="competitor-list-page">
          <div class="print-header">
            <h1 class="tournament-name">{{ tournament.name }}</h1>
            <h2 class="category-title">{{ getBracketTitle(bracket) }}</h2>
            <div v-if="tournament.date" class="tournament-date">
              {{ formatDate(tournament.date) }}
            </div>
          </div>
          <tournament-print-modal
            :category="bracket.category"
            :discipline="bracket.discipline || bracket.teamDiscipline"
            :is-male="getIsMale(bracket)"
            :is-team="isTeamBracket(bracket)"
            :pre-filtered-data="getBracketCompetitors(bracket)"
            :custom-title="getBracketPrintTitle(bracket)"
            :competitors="[]"
          />
        </div>
        <!-- Draw starts on the next page -->
        <div class="bracket-page">
          <div class="print-header">
            <h1 class="tournament-name">{{ tournament.name }}</h1>
            <h2 class="category-title">{{ getBracketTitle(bracket) }}</h2>
            <div v-if="tournament.date" class="tournament-date">
              {{ formatDate(tournament.date) }}
            </div>
          </div>
          <div class="bracket-wrapper">
            <bracket-display
              :bracket="bracket"
              :is-locked="areBracketsLocked"
              :print-team-layout="isTeamBracket(bracket)"
            />
          </div>
        </div>
      </div>
    </template>

    <div v-else-if="!loading && (!tournament || brackets.length === 0)" class="empty-message">
      <p>No brackets available for printing.</p>
    </div>
  </div>
</template>

<script>
import BracketDisplay from '@/components/tournaments/BracketDisplay'
import TournamentPrintModal from '@/components/tournaments/TournamentPrintModal'

export default {
  name: 'PrintBrackets',

  components: {
    BracketDisplay,
    TournamentPrintModal
  },

  data () {
    return {
      error: false,
      loading: false,
      brackets: [],
      tournament: null
    }
  },

  computed: {
    viewingActiveTournament () {
      return this.$route.name === 'ActiveTournamentPrintBrackets'
    },

    activeTournament () {
      return this.$store.state.app.activeTournament
    },

    tournamentId () {
      if (this.viewingActiveTournament) {
        return this.activeTournament?.id
      }
      return this.$route.params.id
    },

    areBracketsLocked () {
      // Handle boolean, number, or string formats from API
      const locked = this.tournament?.brackets_locked
      return locked === true || locked === 1 || locked === '1'
    }
  },

  async created () {
    await this.load()
  },

  mounted () {
    // Auto-trigger print dialog after content loads
    // Use multiple delays to ensure DOM is fully rendered
    this.$nextTick(() => {
      setTimeout(() => {
        // Double-check that content is visible before printing
        if (this.brackets.length > 0 && this.tournament) {
          window.print()
        } else {
          // If content not ready, wait a bit more
          setTimeout(() => {
            window.print()
          }, 500)
        }
      }, 500)
    })
  },

  methods: {
    async load () {
      this.loading = true
      try {
        // Load tournament data
        if (this.viewingActiveTournament) {
          this.tournament = this.activeTournament
        } else {
          await this.$store.dispatch('tournaments/get', this.tournamentId)
          this.tournament = this.$store.getters['tournaments/get'](this.tournamentId)
        }

        // Load brackets
        await this.loadBrackets()
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async loadBrackets () {
      try {
        const response = await this.$store.dispatch(
          'tournaments/getBrackets',
          this.tournamentId
        )
        this.brackets = response || []
      } catch (e) {
        console.error(e)
        this.brackets = []
      }
    },

    getBracketTitle (bracket) {
      const categoryPart = bracket.category?.name || ''
      const disciplinePart = (bracket.discipline || bracket.teamDiscipline)?.name || ''
      const titleParts = [disciplinePart, categoryPart].filter(Boolean).join(' | ')
      if (!titleParts) return 'Bracket'
      if (bracket.gender && bracket.gender !== 'mixed') {
        return `${titleParts} - ${bracket.gender === 'male' ? 'Muški' : 'Ženski'}`
      }
      return titleParts
    },

    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('sr-RS', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getBracketRounds (bracket) {
      const matches = bracket.matches || []
      const roundsMap = {}

      matches.forEach(match => {
        if (!roundsMap[match.round]) {
          roundsMap[match.round] = {
            number: match.round,
            matches: []
          }
        }
        roundsMap[match.round].matches.push(match)
      })

      // Sort matches within each round by matchNumber
      Object.keys(roundsMap).forEach(roundNum => {
        roundsMap[roundNum].matches.sort((a, b) => {
          return (a.matchNumber || 0) - (b.matchNumber || 0)
        })
      })

      // Convert to array and sort by round number
      return Object.values(roundsMap).sort((a, b) => a.number - b.number)
    },

    getBracketCompetitors (bracket) {
      // Get all competitors from the first round of the bracket
      const competitors = []
      const competitorIds = new Set()

      if (!bracket.matches || bracket.matches.length === 0) {
        return []
      }

      // Find the first round
      const rounds = this.getBracketRounds(bracket)
      if (rounds.length === 0) return []

      const firstRound = rounds[0]

      firstRound.matches.forEach(match => {
        // Check both competitorReg and competitorRegId to handle different data structures
        if (match.competitorReg && match.competitorRegId && !competitorIds.has(match.competitorRegId)) {
          const competitor = { ...match.competitorReg, _id: match.competitorRegId }
          competitors.push(competitor)
          competitorIds.add(match.competitorRegId)
        }
        if (match.competitorTwoReg && match.competitorTwoRegId && !competitorIds.has(match.competitorTwoRegId)) {
          const competitor = { ...match.competitorTwoReg, _id: match.competitorTwoRegId }
          competitors.push(competitor)
          competitorIds.add(match.competitorTwoRegId)
        }
      })

      // Sort by assigned number if available, otherwise by name
      return competitors.sort((a, b) => {
        const numA = a.assignedNumber ? parseInt(a.assignedNumber) : 9999
        const numB = b.assignedNumber ? parseInt(b.assignedNumber) : 9999
        if (numA !== numB) return numA - numB

        const nameA = a.competitor ? `${a.competitor.firstName || ''} ${a.competitor.lastName || ''}`.trim() : ''
        const nameB = b.competitor ? `${b.competitor.firstName || ''} ${b.competitor.lastName || ''}`.trim() : ''
        return nameA.localeCompare(nameB)
      })
    },

    getBracketPrintTitle (bracket) {
      const parts = []

      // Add discipline name
      const discipline = bracket.discipline || bracket.teamDiscipline
      if (discipline?.name) {
        parts.push(discipline.name)
      }

      // Add category name
      if (bracket.category?.name) {
        parts.push(bracket.category.name)
      }

      // Add gender
      if (bracket.gender && bracket.gender !== 'mixed') {
        parts.push(bracket.gender === 'male' ? 'Muški' : 'Ženski')
      }

      return parts.join(' - ') || 'Bracket'
    },

    getGenderText (bracket) {
      if (!bracket.gender || bracket.gender === 'mixed') {
        return ''
      }
      return bracket.gender === 'male' ? 'Muški' : 'Ženski'
    },

    getIsMale (bracket) {
      if (!bracket.gender || bracket.gender === 'mixed') {
        return null
      }
      return bracket.gender === 'male'
    },

    isTeamBracket (bracket) {
      return bracket.teamDiscipline !== null &&
             bracket.teamDiscipline !== undefined &&
             bracket.teamDiscipline !== '' &&
             bracket.teamDiscipline !== 0
    }
  }
}
</script>

<style lang="scss" scoped>
.print-root {
  min-height: 100vh;
  width: 100%;
}

.print-brackets-page {
  padding: 20px;
  background: #fff;
  height: auto;
  max-height: none;
  overflow: visible;
  position: static;
}

.print-category {
  break-after: page;
  page-break-after: always;
  padding: 0;
  margin: 0;
  margin-bottom: 0;

  &:last-child {
    break-after: auto;
    page-break-after: auto;
  }
}

.print-header {
  margin-bottom: 20px;
  page-break-after: avoid;
  break-after: avoid;
  padding-bottom: 15px;
  border-bottom: 2px solid #333;

  .tournament-name {
    margin: 0 0 10px 0;
    font-size: 24px;
    font-weight: bold;
    color: #333;
    break-after: avoid;
    page-break-after: avoid;
  }

  .category-title {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 600;
    color: #555;
    break-after: avoid;
    page-break-after: avoid;
  }

  .tournament-date {
    font-size: 14px;
    color: #666;
    margin-top: 5px;
  }
}

.bracket-page {
  break-before: page;
  page-break-before: always;
}

.bracket-wrapper {
  break-inside: avoid;
  page-break-inside: avoid;
  margin-top: 20px;
}

.error-message,
.loading-message,
.empty-message {
  padding: 40px;
  text-align: center;
  font-size: 16px;
  color: #666;
}

// Print styles
@media print {
  html, body {
    height: auto !important;
    overflow: visible !important;
  }

  body {
    position: static !important;
    background: #fff !important;
  }

  /* Disable any app-level wrappers */
  .app,
  .layout,
  .main-layout,
  .page-wrapper,
  .content-wrapper {
    all: unset !important;
    display: block !important;
    height: auto !important;
    overflow: visible !important;
    position: static !important;
  }

  .no-print {
    display: none !important;
  }

  .print-root,
  .print-brackets-page,
  .print-category {
    display: block !important;
    visibility: visible !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    position: static !important;
  }

  .print-brackets-page {
    padding: 0;
    background: #fff !important;
  }

  .print-category {
    break-after: page;
    page-break-after: always;
    padding: 0;
    margin: 0;
    margin-bottom: 0;

    &:last-child {
      break-after: auto;
      page-break-after: auto;
    }
  }

  .print-header {
    margin-bottom: 12px;
    page-break-after: avoid;
    break-after: avoid;
    padding-bottom: 10px;
    border-bottom: 2px solid #000;

    .tournament-name {
      break-after: avoid;
      page-break-after: avoid;
    }

    .category-title {
      break-after: avoid;
      page-break-after: avoid;
    }
  }

  .bracket-page {
    break-before: page;
    page-break-before: always;
  }

  .bracket-wrapper {
    break-inside: avoid;
    page-break-inside: avoid;
    transform: none !important;
    margin-top: 15px;
  }

  h1, h2, h3 {
    break-after: avoid;
    page-break-after: avoid;
  }

  .error-message,
  .loading-message,
  .empty-message {
    display: none !important;
  }
}

// Hide non-print elements globally in print mode
@media print {
  // Hide layout elements (if they exist)
  ::v-deep .sidebar,
  ::v-deep .page-header,
  ::v-deep .no-print,
  ::v-deep nav,
  ::v-deep .el-button,
  ::v-deep .el-alert,
  ::v-deep .panel-actions-row,
  ::v-deep .page-wrapper > .sidebar,
  ::v-deep .page-wrapper > main > .page-header {
    display: none !important;
  }
}
</style>

<style lang="scss">
/* Global print styles - not scoped to ensure they apply */
@media print {
  html, body {
    height: auto !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  body {
    position: static !important;
    background: #fff !important;
  }

  /* Disable any app-level wrappers that might exist */
  .app,
  .layout,
  .main-layout,
  .page-wrapper,
  .content-wrapper,
  #app {
    all: unset !important;
    display: block !important;
    height: auto !important;
    overflow: visible !important;
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Ensure print content is visible */
  .print-root,
  .print-brackets-page {
    display: block !important;
    visibility: visible !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
  }
}
</style>
